import _ from 'lodash';
import React, { useCallback, useContext, useRef } from 'react';
import styled from 'styled-components';

import AttachmentButton from 'components/lib/attachment/attachment_button';
import Emoji from 'components/composer/shared/emoji';
import ComposerContext from 'components/composer/contexts/composer_context';
import DynamicOverflow from 'components/common/dynamic_overflow';
import FontSizeStyles from 'components/customer/composition/lib/slate/slate_font_size_menu';
import SlateFontColorMenu from 'components/customer/composition/lib/slate/slate_font_color_menu';
import HotkeyLabel from 'components/hotkeys/hotkey_label';
import { Item, Menu, Separator } from 'components/customer/composition/lib/slate/slate_styles_menu.styles';
import { ORDERED_LIST, UNORDERED_LIST } from 'components/text_editor/plugins/lists/constants';
import OverflowFillIcon from 'components/common/icons/fill/overflow-fill';
import TextAlignmentStyles from 'components/customer/composition/lib/slate/slate_alignment_menu';
import {
  SlateTextDecreaseIndentationMenu,
  SlateTextIncreaseIndentationMenu,
} from 'components/customer/composition/lib/slate/slate_text_indentation_menu';
import Tooltip from 'components/common/tooltip';
import StartUpload from 'actions/composition/start_upload';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import QuotedTextIcon from 'components/common/icons/quoted_text_icon';
import { QUOTEBLOCK } from 'components/text_editor/plugins/quote_blocks';

export function RichTextEditorStyles(props) {
  return (
    <StyledMenu>
      <RichTextStyles {...props} />
    </StyledMenu>
  );
}

const StyledMenu = styled(Menu)`
  flex-grow: 1;
  display: flex;
  overflow: auto;
`;

export function RichTextStyles({ children, editorRef, onChange, value }) {
  const stylesProps = { editor: editorRef.current, onChange, value };

  const menuItems = [
    <BoldToggle alwaysShow {...stylesProps} />,
    <ItalicToggle alwaysShow {...stylesProps} />,
    <UnderlineToggle alwaysShow {...stylesProps} />,
    <FontSizeStyles {...stylesProps} />,
    <SlateFontColorMenu {...stylesProps} />,
    <OrderedListToggle {...stylesProps} />,
    <UnorderedListToggle {...stylesProps} />,
    <TextAlignmentStyles {...stylesProps} />,
    <SlateTextDecreaseIndentationMenu {...stylesProps} />,
    <SlateTextIncreaseIndentationMenu {...stylesProps} />,
    <LinkStyles {...stylesProps} />,
    <QuoteStyles {...stylesProps} />,
    ...children,
  ];

  return (
    <DynamicOverflow
      defaultItemWidth={38}
      itemRenderer={(visibleItems, overflowItems) => {
        // remove two consecutive separators
        visibleItems = visibleItems.reduce(
          (acc, cur) => {
            if (!(acc.prev && cur.props.children.type === acc.prev.props.children.type)) {
              acc.result.push(cur);
              acc.prev = cur;
            }
            return acc;
          },
          { result: [] }
        ).result;

        return (
          <React.Fragment>
            {visibleItems}
            {overflowItems.length > 0 && <RichTextStylesOverflowMenu>{overflowItems}</RichTextStylesOverflowMenu>}
          </React.Fragment>
        );
      }}
    >
      {menuItems}
    </DynamicOverflow>
  );
}

import PopoverMenu, { PopoverMenuItem, usePopoverMenu } from 'components/common/menu';

const StyledPopoverMenu = styled(PopoverMenu)`
  overflow: visible;
`;

const RichTextStylesOverflowMenu = ({ children }) => {
  const { targetRef, isOpen, onClose, onToggle, setTargetRef } = usePopoverMenu();
  return (
    <React.Fragment>
      <Separator />
      <Item onClick={onToggle} ref={setTargetRef}>
        <MoreMenuItem isOpen={isOpen} />
      </Item>
      <StyledPopoverMenu
        margin={0}
        isOpen={isOpen}
        onClose={onClose}
        position="top"
        targetPosition="center"
        targetRef={targetRef}
        boundByWindow
      >
        <StyledPopoverMenuItem onClose={_.noop}>{children}</StyledPopoverMenuItem>
      </StyledPopoverMenu>
    </React.Fragment>
  );
};

const StyledPopoverMenuItem = styled(PopoverMenuItem)`
  flex-direction: row;
  min-width: 0;
  padding: 8px;
  &:hover {
    background: none;
  }
`;

const MoreMenuItem = styled(OverflowFillIcon)`
  stroke: ${p => (p.isOpen ? p.theme.colors.green400 : p.theme.colors.black)};
  fill: ${p => (p.isOpen ? p.theme.colors.green400 : p.theme.colors.gray900)};
  height: 18px;
  width: 13px;
  line-height: 0;
`;

const BoldToggle = styled(function({ className, editor, onChange, value }) {
  const toggleBold = useEditorChange(editor, onChange, () => editor.toggleMark('bold'));
  return (
    <Item
      className={className}
      data-aid="bold"
      isActive={isMarkActive(value, 'bold')}
      onClick={toggleBold}
      title="Bold"
    >
      B
    </Item>
  );
})`
  font-weight: bold;
`;

const ItalicToggle = styled(function({ className, editor, onChange, value }) {
  const toggleItalic = useEditorChange(editor, onChange, () => editor.toggleMark('italic'));
  return (
    <Item
      className={className}
      data-aid="italic"
      isActive={isMarkActive(value, 'italic')}
      onClick={toggleItalic}
      title="Italic"
    >
      i
    </Item>
  );
})`
  font-style: italic;
`;

const UnderlineToggle = styled(function({ className, editor, onChange, value }) {
  const toggleUnderline = useEditorChange(editor, onChange, () => editor.toggleMark('underline'));
  return (
    <Item
      className={className}
      data-aid="underline"
      isActive={isMarkActive(value, 'underline')}
      onClick={toggleUnderline}
      title="Underline"
    >
      U
    </Item>
  );
})`
  text-decoration: underline;
`;

function OrderedListToggle({ editor, onChange, value }) {
  const toggleOrderedList = useEditorChange(editor, onChange, () => editor.toggleList('ordered_list'));
  return (
    <Item
      data-aid="orderedList"
      isActive={isListActive(value, 'ordered_list')}
      onClick={toggleOrderedList}
      title="Ordered list"
    >
      <i className="fa fa-list-ol" />
    </Item>
  );
}

function UnorderedListToggle({ editor, onChange, value }) {
  const toggleUnorderedList = useEditorChange(editor, onChange, () => editor.toggleList('unordered_list'));
  return (
    <Item
      data-aid="unorderedList"
      isActive={isListActive(value, 'unordered_list')}
      onClick={toggleUnorderedList}
      title="Unordered list"
    >
      <i className="fa fa-list-ul" />
    </Item>
  );
}

export function EmojiStyles({ editorRef }) {
  const editor = editorRef.current;
  return <Emoji editor={editor} />;
}

export function AttachmentStyles() {
  const attachmentsRef = useRef(null);

  const { composition } = useContext(ComposerContext);
  const executeAction = useExecuteAction();
  const uploadFile = useCallback(
    file =>
      executeAction(StartUpload, {
        compositionId: composition.id,
        file,
      }),
    [composition.id, executeAction]
  );

  const onChange = useCallback(() => {
    const files = attachmentsRef.current.input.files;
    for (let i = 0; i < files.length; i++) {
      uploadFile(files[i]);
    }
  }, [uploadFile]);

  return <AttachmentButton onAttach={onChange} ref={attachmentsRef} />;
}

export function LinkStyles({ editor, onChange, value }) {
  const toggleLink = useCallback(
    evt => {
      evt.preventDefault();
      onChange(editor.wrapInline({ type: 'link' }).blur());
    },
    [editor, onChange]
  );

  return (
    <Tooltip delay={500} message={<HotkeyLabel hotkey="ctrl+k" label="Insert Link" />} position="top">
      <Item data-aid="link" isActive={isInlineActive(value, 'link')} onMouseDown={toggleLink}>
        <i className="fa fa-link" />
      </Item>
    </Tooltip>
  );
}

export function QuoteStyles({ editor, onChange, value }) {
  const toggleQuote = useEditorChange(editor, onChange, () => editor.toggleQuote());

  return (
    <Tooltip delay={500} message="Quote Text" position="top">
      <Item data-aid="quote" isActive={isQuotedTextActive(value)} onMouseDown={toggleQuote}>
        <QuotedTextIcon />
      </Item>
    </Tooltip>
  );
}

function useEditorChange(editor, onChange, callback) {
  return useCallback(
    evt => {
      evt.preventDefault();
      editor.focus();
      return onChange(callback());
    },
    [callback, editor, onChange]
  );
}

function isMarkActive(value, style) {
  let isActive = value.activeMarks.some(mark => mark.type === style);
  return isActive;
}

function isListActive(value, listType) {
  let document = value && value.document;
  return value.blocks.some(block => {
    const closestList = document.getClosest(
      block.key,
      parent => parent.type === UNORDERED_LIST || parent.type === ORDERED_LIST
    );
    return closestList && closestList.type === listType;
  });
}

function isQuotedTextActive(value) {
  let isQuoteBlockType = value.blocks.some(block => {
    return !!value.document.getClosest(block.key, parent => parent.type === QUOTEBLOCK);
  });
  return isQuoteBlockType;
}

function isInlineActive(value, type) {
  const isFocused = value.inlines.some(inline => inline.type === type);
  return isFocused;
}

export { Separator };



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/composer/shared/editor_styles.jsx